import type { Breadcrumb } from '@sentry/react'

/**
 * Lazy front door to Sentry.
 *
 * @sentry/react is ~80 KB and cost ~680 ms of main-thread time on a mid-range
 * phone when it was initialized synchronously before React rendered
 * (Lighthouse, Sep 2026). Nothing in the first paint needs it, so:
 *
 *   - scheduleSentry() loads and initializes it after the window load event,
 *     when the browser is idle.
 *   - reportError / reportException / reportBreadcrumb load it on demand, so
 *     an error that happens BEFORE the scheduled init still gets reported;
 *     it just pays the load cost at that moment.
 *   - installEarlyErrorCapture() catches uncaught errors and rejections that
 *     fire before Sentry's own global handlers exist. It removes itself once
 *     Sentry is up so nothing is double-reported.
 *
 * Trade-off accepted: an error in the first few hundred milliseconds is
 * reported slightly late rather than not at all.
 */

type SentryModule = typeof import('./sentry')

let sentryPromise: Promise<SentryModule> | null = null

function onEarlyError(event: ErrorEvent): void {
  reportError(event.error ?? event.message, { where: 'early-window-error' })
}

function onEarlyRejection(event: PromiseRejectionEvent): void {
  reportError(event.reason, { where: 'early-unhandled-rejection' })
}

function uninstallEarlyErrorCapture(): void {
  if (typeof window === 'undefined') return
  window.removeEventListener('error', onEarlyError)
  window.removeEventListener('unhandledrejection', onEarlyRejection)
}

export function installEarlyErrorCapture(): void {
  if (typeof window === 'undefined') return
  window.addEventListener('error', onEarlyError)
  window.addEventListener('unhandledrejection', onEarlyRejection)
}

export function loadSentry(): Promise<SentryModule> {
  if (!sentryPromise) {
    sentryPromise = import('./sentry').then((mod) => {
      mod.initSentry()
      uninstallEarlyErrorCapture()
      return mod
    })
  }
  return sentryPromise
}

/** Initialize Sentry once the page has loaded and the main thread is idle. */
export function scheduleSentry(): void {
  if (typeof window === 'undefined') return
  const start = () => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(() => void loadSentry(), { timeout: 4000 })
    } else {
      window.setTimeout(() => void loadSentry(), 2000)
    }
  }
  if (document.readyState === 'complete') start()
  else window.addEventListener('load', start, { once: true })
}

/**
 * Capture an error with optional extra context. Safe to call at any time;
 * if Sentry is not configured (no DSN) this is a no-op after the module loads.
 */
export function reportError(error: unknown, context?: Record<string, unknown>): void {
  void loadSentry()
    .then((mod) => mod.captureError(error, context))
    .catch(() => {})
}

export function reportException(
  error: unknown,
  hint?: Parameters<SentryModule['captureException']>[1],
): void {
  void loadSentry()
    .then((mod) => mod.captureException(error, hint))
    .catch(() => {})
}

export function reportBreadcrumb(breadcrumb: Breadcrumb): void {
  void loadSentry()
    .then((mod) => mod.addBreadcrumb(breadcrumb))
    .catch(() => {})
}
